import React, { useState, useEffect } from "react";
import {
  Badge,
  Button,
  Card,
  Col,
  Descriptions,
  Empty,
  Form,
  Input,
  message,
  Modal,
  Row,
  Space,
  Table,
  Tag,
  Typography,
} from "antd";
import { CheckOutlined, CloseOutlined, SendOutlined } from "@ant-design/icons";
import { useTranslation } from "react-i18next";
import { useFirestore } from "react-redux-firebase";
import { useSelector } from "react-redux";
import * as dayjs from "dayjs";
import { Link } from "react-router-dom";
import {
  FIRESTORE_PRODUCT_DRAFTS_TABLE,
  FIRESTORE_PRODUCTS_TABLE,
} from "../../../../constants/FirestoreConstant";
import {
  PRODUCT_DRAFT_STATUS_APPROVED,
  PRODUCT_DRAFT_STATUS_CHANGES_REQUESTED,
  PRODUCT_DRAFT_STATUS_NEEDS_SUPPLIER_INPUT,
  PRODUCT_DRAFT_STATUS_PUBLISHED,
  PRODUCT_DRAFT_STATUS_SUBMITTED,
} from "../../../../constants/OrderConstant";
import urls from "../../../../urls";

const { Text } = Typography;

const getEditableFields = (t) => [
  { key: "purchasePrice", label: t("product_draft.field_purchase_price"), type: "currency" },
  { key: "innerPackagingQuantity", label: t("product_draft.field_inner_packaging"), type: "number" },
  { key: "masterPackagingQuantity", label: t("product_draft.field_master_packaging"), type: "number" },
  { key: "length", label: t("product_draft.field_length"), type: "number" },
  { key: "width", label: t("product_draft.field_width"), type: "number" },
  { key: "height", label: t("product_draft.field_height"), type: "number" },
  { key: "diameter", label: t("product_draft.field_diameter"), type: "number" },
  { key: "color", label: t("product_draft.field_color"), type: "text" },
];

const ProductDrafts = () => {
  const { t } = useTranslation();
  const firestore = useFirestore();
  const [messageApi, contextHolder] = message.useMessage();
  const [productDrafts, setProductDrafts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedDraft, setSelectedDraft] = useState(null);
  const [detailModalOpen, setDetailModalOpen] = useState(false);
  const [rejectModalOpen, setRejectModalOpen] = useState(false);
  const [rejectFeedback, setRejectFeedback] = useState("");
  const [actionLoading, setActionLoading] = useState(false);
  const profile = useSelector((state) => state.firebaseReducer.profile);
  const { email } = profile;

  useEffect(() => {
    const unsubscribe = firestore
      .collection(FIRESTORE_PRODUCT_DRAFTS_TABLE)
      .orderBy("createdAt", "desc")
      .onSnapshot(
        (snapshot) => {
          const drafts = snapshot.docs.map((doc) => ({
            id: doc.id,
            ...doc.data(),
          }));
          setProductDrafts(drafts);
          setLoading(false);
        },
        (error) => {
          console.error("Error fetching product drafts:", error);
          setLoading(false);
        }
      );

    return () => unsubscribe();
  }, [firestore]);

  const getStatusColor = (status) => {
    switch (status) {
      case PRODUCT_DRAFT_STATUS_NEEDS_SUPPLIER_INPUT:
        return "default";
      case PRODUCT_DRAFT_STATUS_SUBMITTED:
        return "processing";
      case PRODUCT_DRAFT_STATUS_CHANGES_REQUESTED:
        return "warning";
      case PRODUCT_DRAFT_STATUS_APPROVED:
        return "success";
      case PRODUCT_DRAFT_STATUS_PUBLISHED:
        return "green";
      default:
        return "default";
    }
  };

  const getStatusText = (status) => {
    switch (status) {
      case PRODUCT_DRAFT_STATUS_NEEDS_SUPPLIER_INPUT:
        return t("product_draft.needs_supplier_input");
      case PRODUCT_DRAFT_STATUS_SUBMITTED:
        return t("product_draft.submitted");
      case PRODUCT_DRAFT_STATUS_CHANGES_REQUESTED:
        return t("product_draft.changes_requested");
      case PRODUCT_DRAFT_STATUS_APPROVED:
        return t("product_draft.approved");
      case PRODUCT_DRAFT_STATUS_PUBLISHED:
        return t("product_draft.published");
      default:
        return status;
    }
  };

  const handleApprove = async (draft) => {
    setActionLoading(true);
    try {
      const draftProduct = draft.draftProduct || {};

      // Update the draft status
      await firestore.collection(FIRESTORE_PRODUCT_DRAFTS_TABLE).doc(draft.id).update({
        status: PRODUCT_DRAFT_STATUS_APPROVED,
        reviewedAt: dayjs().unix(),
        reviewedBy: email,
        reviewFeedback: null,
        updatedAt: dayjs().unix(),
        updatedBy: email,
      });

      // Add revision
      await firestore
        .collection(FIRESTORE_PRODUCT_DRAFTS_TABLE)
        .doc(draft.id)
        .collection("revisions")
        .add({
          type: "BOMBYXX_APPROVE",
          timestamp: dayjs().unix(),
          user: email,
          draftProductSnapshot: draftProduct,
        });

      // Update the purchase order product with the draft data
      if (draft.purchaseOrderId) {
        const poRef = firestore.collection("purchaseOrders").doc(draft.purchaseOrderId);
        const poDoc = await poRef.get();
        if (poDoc.exists) {
          const poData = poDoc.data();
          const updatedProducts = (poData.products || []).map((p, index) => {
            if (index === draft.purchaseOrderProductIndex && p.productDraftId === draft.id) {
              return {
                ...p,
                // Update with approved draft data
                price: draftProduct.purchasePrice || p.price,
                innerPackagingQuantity: draftProduct.innerPackagingQuantity || p.innerPackagingQuantity,
                masterPackagingQuantity: draftProduct.masterPackagingQuantity || p.masterPackagingQuantity,
                productDimensions: {
                  length: draftProduct.length || null,
                  width: draftProduct.width || null,
                  height: draftProduct.height || null,
                  diameter: draftProduct.diameter || null,
                },
                productColor: draftProduct.color || p.productColor,
              };
            }
            return p;
          });
          await poRef.update({ 
            products: updatedProducts,
            updatedAt: dayjs().unix(),
            updatedBy: email,
          });
        }
      }

      // If there's an existing product (published), update it with the new data
      if (draft.publishedProductId) {
        const productUpdateData = {};
        if (draftProduct.purchasePrice !== undefined && draftProduct.purchasePrice !== null) {
          productUpdateData.purchasePrice = draftProduct.purchasePrice;
        }
        if (draftProduct.innerPackagingQuantity !== undefined && draftProduct.innerPackagingQuantity !== null) {
          productUpdateData.innerPackagingQuantity = draftProduct.innerPackagingQuantity;
        }
        if (draftProduct.masterPackagingQuantity !== undefined && draftProduct.masterPackagingQuantity !== null) {
          productUpdateData.masterPackagingQuantity = draftProduct.masterPackagingQuantity;
        }
        if (draftProduct.length !== undefined && draftProduct.length !== null) {
          productUpdateData.length = draftProduct.length;
        }
        if (draftProduct.width !== undefined && draftProduct.width !== null) {
          productUpdateData.width = draftProduct.width;
        }
        if (draftProduct.height !== undefined && draftProduct.height !== null) {
          productUpdateData.height = draftProduct.height;
        }
        if (draftProduct.diameter !== undefined && draftProduct.diameter !== null) {
          productUpdateData.diameter = draftProduct.diameter;
        }
        if (draftProduct.color !== undefined && draftProduct.color !== null) {
          productUpdateData.color = draftProduct.color;
        }

        if (Object.keys(productUpdateData).length > 0) {
          productUpdateData.updatedAt = dayjs().unix();
          productUpdateData.updatedBy = email;
          await firestore.collection(FIRESTORE_PRODUCTS_TABLE).doc(draft.publishedProductId).update(productUpdateData);
        }
      }

      messageApi.success(t("product_draft.approve_success"));
      setDetailModalOpen(false);
      setSelectedDraft(null);
    } catch (error) {
      console.error("Error approving draft:", error);
      messageApi.error(t("product_draft.approve_failed"));
    } finally {
      setActionLoading(false);
    }
  };

  const handleReject = async () => {
    if (!rejectFeedback.trim()) {
      messageApi.error(t("product_draft.feedback_required"));
      return;
    }

    setActionLoading(true);
    try {
      await firestore.collection(FIRESTORE_PRODUCT_DRAFTS_TABLE).doc(selectedDraft.id).update({
        status: PRODUCT_DRAFT_STATUS_CHANGES_REQUESTED,
        reviewedAt: dayjs().unix(),
        reviewedBy: email,
        reviewFeedback: rejectFeedback,
        updatedAt: dayjs().unix(),
        updatedBy: email,
      });

      await firestore
        .collection(FIRESTORE_PRODUCT_DRAFTS_TABLE)
        .doc(selectedDraft.id)
        .collection("revisions")
        .add({
          type: "BOMBYXX_REJECT",
          timestamp: dayjs().unix(),
          user: email,
          draftProductSnapshot: selectedDraft.draftProduct || {},
          comment: rejectFeedback,
        });

      messageApi.success(t("product_draft.reject_success"));
      setRejectModalOpen(false);
      setDetailModalOpen(false);
      setSelectedDraft(null);
      setRejectFeedback("");
    } catch (error) {
      console.error("Error rejecting draft:", error);
      messageApi.error(t("product_draft.reject_failed"));
    } finally {
      setActionLoading(false);
    }
  };

  const handlePublish = async (draft) => {
    setActionLoading(true);
    try {
      const productsSnapshot = await firestore
        .collection(FIRESTORE_PRODUCTS_TABLE)
        .where("code", "==", draft.productCode)
        .get();

      if (!productsSnapshot.empty) {
        messageApi.error(t("product_draft.product_code_exists", { code: draft.productCode }));
        setActionLoading(false);
        return;
      }

      const newProductData = {
        name: draft.productName,
        code: draft.productCode,
        barcode: draft.productBarcode || null,
        url: draft.productName
          .toLowerCase()
          .replace(/[^a-z0-9]+/g, "-")
          .replace(/(^-|-$)/g, ""),
        ...draft.draftProduct,
        stock: 0,
        discount: 0,
        taxRate: 21,
        min_amount: 1,
        createdAt: dayjs().unix(),
        createdBy: email,
      };

      const productRef = await firestore.collection(FIRESTORE_PRODUCTS_TABLE).add(newProductData);

      await firestore.collection(FIRESTORE_PRODUCT_DRAFTS_TABLE).doc(draft.id).update({
        status: PRODUCT_DRAFT_STATUS_PUBLISHED,
        publishedProductId: productRef.id,
        publishedAt: dayjs().unix(),
        publishedBy: email,
        updatedAt: dayjs().unix(),
        updatedBy: email,
      });

      await firestore
        .collection(FIRESTORE_PRODUCT_DRAFTS_TABLE)
        .doc(draft.id)
        .collection("revisions")
        .add({
          type: "PUBLISH",
          timestamp: dayjs().unix(),
          user: email,
          draftProductSnapshot: draft.draftProduct || {},
        });

      if (draft.purchaseOrderId) {
        const poRef = firestore.collection("purchaseOrders").doc(draft.purchaseOrderId);
        const poDoc = await poRef.get();
        if (poDoc.exists) {
          const poData = poDoc.data();
          const updatedProducts = (poData.products || []).map((p, index) => {
            if (index === draft.purchaseOrderProductIndex && p.productDraftId === draft.id) {
              return {
                ...p,
                productId: productRef.id,
              };
            }
            return p;
          });
          await poRef.update({ products: updatedProducts });
        }
      }

      messageApi.success(t("product_draft.publish_success"));
      setDetailModalOpen(false);
      setSelectedDraft(null);
    } catch (error) {
      console.error("Error publishing draft:", error);
      messageApi.error(t("product_draft.publish_failed"));
    } finally {
      setActionLoading(false);
    }
  };

  const openDetailModal = (draft) => {
    setSelectedDraft(draft);
    setDetailModalOpen(true);
  };

  const renderFieldValue = (value, type) => {
    if (value === null || value === undefined || value === "") {
      return <Text type="secondary">{t("product_draft.field_empty")}</Text>;
    }
    if (type === "currency") {
      return `$ ${Number(value).toFixed(2)}`;
    }
    return String(value);
  };

  const columns = [
    {
      title: t("product_draft.status"),
      dataIndex: "status",
      key: "status",
      width: 180,
      filters: [
        { text: t("product_draft.needs_supplier_input"), value: PRODUCT_DRAFT_STATUS_NEEDS_SUPPLIER_INPUT },
        { text: t("product_draft.submitted"), value: PRODUCT_DRAFT_STATUS_SUBMITTED },
        { text: t("product_draft.changes_requested"), value: PRODUCT_DRAFT_STATUS_CHANGES_REQUESTED },
        { text: t("product_draft.approved"), value: PRODUCT_DRAFT_STATUS_APPROVED },
        { text: t("product_draft.published"), value: PRODUCT_DRAFT_STATUS_PUBLISHED },
      ],
      onFilter: (value, record) => record.status === value,
      render: (status) => <Tag color={getStatusColor(status)}>{getStatusText(status)}</Tag>,
    },
    {
      title: t("product"),
      key: "product",
      render: (_, record) => (
        <div>
          <div style={{ fontWeight: 500 }}>{record.productName}</div>
          <Text type="secondary" style={{ fontSize: 12 }}>
            {record.productCode}
          </Text>
        </div>
      ),
    },
    {
      title: t("product_draft.purchase_order"),
      dataIndex: "purchaseOrderId",
      key: "purchaseOrder",
      render: (poId) =>
        poId ? (
          <Link to={`${urls.purchaseOrdersList}/${poId}`}>{t("product_draft.view_order")}</Link>
        ) : (
          "-"
        ),
    },
    {
      title: t("product_draft.submitted_at"),
      dataIndex: "supplierSubmittedAt",
      key: "submittedAt",
      render: (timestamp) => (timestamp ? dayjs.unix(timestamp).format("DD-MM-YYYY HH:mm") : "-"),
      sorter: (a, b) => (a.supplierSubmittedAt || 0) - (b.supplierSubmittedAt || 0),
    },
    {
      title: t("created_at"),
      dataIndex: "createdAt",
      key: "createdAt",
      render: (timestamp) => dayjs.unix(timestamp).format("DD-MM-YYYY HH:mm"),
      sorter: (a, b) => a.createdAt - b.createdAt,
      defaultSortOrder: "descend",
    },
    {
      title: "",
      key: "actions",
      width: 120,
      render: (_, record) => (
        <Button type="link" onClick={() => openDetailModal(record)}>
          {t("product_draft.view_changes")}
        </Button>
      ),
    },
  ];

  return (
    <>
      {contextHolder}
      <h2>{t("product_draft.title")}</h2>
      <Card>
        <Table
          columns={columns}
          dataSource={productDrafts}
          rowKey="id"
          loading={loading}
          locale={{ emptyText: <Empty description={t("product_draft.no_drafts")} /> }}
        />
      </Card>

      <Modal
        title={selectedDraft?.productName}
        open={detailModalOpen}
        onCancel={() => {
          setDetailModalOpen(false);
          setSelectedDraft(null);
        }}
        width={800}
        footer={
          selectedDraft?.status === PRODUCT_DRAFT_STATUS_SUBMITTED
            ? [
                <Button
                  key="reject"
                  danger
                  icon={<CloseOutlined />}
                  onClick={() => setRejectModalOpen(true)}
                  loading={actionLoading}
                >
                  {t("product_draft.reject")}
                </Button>,
                <Button
                  key="approve"
                  type="primary"
                  icon={<CheckOutlined />}
                  onClick={() => handleApprove(selectedDraft)}
                  loading={actionLoading}
                >
                  {t("product_draft.approve")}
                </Button>,
              ]
            : selectedDraft?.status === PRODUCT_DRAFT_STATUS_APPROVED
              ? [
                  <Button
                    key="publish"
                    type="primary"
                    icon={<SendOutlined />}
                    onClick={() => handlePublish(selectedDraft)}
                    loading={actionLoading}
                  >
                    {t("product_draft.publish")}
                  </Button>,
                ]
              : null
        }
      >
        {selectedDraft && (
          <div>
            <Descriptions bordered size="small" column={2} style={{ marginBottom: 24 }}>
              <Descriptions.Item label={t("product_draft.draft_product_name")}>
                {selectedDraft.productName}
              </Descriptions.Item>
              <Descriptions.Item label={t("product_draft.draft_product_code")}>
                {selectedDraft.productCode}
              </Descriptions.Item>
              <Descriptions.Item label={t("product_draft.draft_product_barcode")}>
                {selectedDraft.productBarcode || "-"}
              </Descriptions.Item>
              <Descriptions.Item label={t("product_draft.status")}>
                <Tag color={getStatusColor(selectedDraft.status)}>{getStatusText(selectedDraft.status)}</Tag>
              </Descriptions.Item>
              {selectedDraft.supplierSubmittedAt && (
                <Descriptions.Item label={t("product_draft.submitted_at")}>
                  {dayjs.unix(selectedDraft.supplierSubmittedAt).format("DD-MM-YYYY HH:mm")}
                </Descriptions.Item>
              )}
              {selectedDraft.reviewFeedback && (
                <Descriptions.Item label={t("product_draft.review_feedback")} span={2}>
                  <Text type="warning">{selectedDraft.reviewFeedback}</Text>
                </Descriptions.Item>
              )}
            </Descriptions>

            <h4>{t("product_draft.supplier_data")}</h4>
            <Row gutter={[16, 16]}>
              {getEditableFields(t).map((field) => (
                <Col span={12} key={field.key}>
                  <Card size="small">
                    <div style={{ fontSize: 12, color: "#666", marginBottom: 4 }}>{field.label}</div>
                    <div style={{ fontWeight: 500 }}>
                      {renderFieldValue(selectedDraft.draftProduct?.[field.key], field.type)}
                    </div>
                  </Card>
                </Col>
              ))}
            </Row>

            {selectedDraft.draftProduct?.image && selectedDraft.draftProduct.image.length > 0 && (
              <div style={{ marginTop: 24 }}>
                <h4>{t("images")}</h4>
                <Space wrap>
                  {selectedDraft.draftProduct.image.map((url, index) => (
                    <img
                      key={index}
                      src={url}
                      alt={`Product ${index + 1}`}
                      style={{ width: 100, height: 100, objectFit: "cover", borderRadius: 4 }}
                    />
                  ))}
                </Space>
              </div>
            )}
          </div>
        )}
      </Modal>

      <Modal
        title={t("product_draft.reject")}
        open={rejectModalOpen}
        onCancel={() => {
          setRejectModalOpen(false);
          setRejectFeedback("");
        }}
        onOk={handleReject}
        okText={t("product_draft.reject")}
        okButtonProps={{ danger: true, loading: actionLoading }}
      >
        <Form layout="vertical">
          <Form.Item label={t("product_draft.review_feedback")} required>
            <Input.TextArea
              rows={4}
              value={rejectFeedback}
              onChange={(e) => setRejectFeedback(e.target.value)}
              placeholder={t("product_draft.feedback_placeholder")}
            />
          </Form.Item>
        </Form>
      </Modal>
    </>
  );
};

export default ProductDrafts;
